import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserAuthService } from '../UserAuth.service';
import { PreferencesService } from './preferences.service';
import { ProjectService } from './project.service';
import { Alert, AlertStateFilter, AlertSummary } from '../models/alert';

/**
 * Cure alert API client + notification-bell summary polling.
 * Mirrors the PreferencesService param/auth conventions
 * (tenantId/objectId/customerId + function key as `code`).
 */
@Injectable({
  providedIn: 'root',
})
export class AlertsService implements OnDestroy {
  /** Poll cadence for the bell summary. */
  static readonly POLL_INTERVAL_MS = 60_000;

  summary$ = new BehaviorSubject<AlertSummary | null>(null);

  private stopPolling$ = new Subject<void>();
  private destroyed$ = new Subject<void>();
  private polling = false;
  private lastCustomerId: string | null = null;

  constructor(
    private http: HttpClient,
    private userAuth: UserAuthService,
    private preferencesService: PreferencesService,
    private projectService: ProjectService,
  ) {}

  /**
   * The project currently on screen, or '' when none is loaded.
   *
   * Alerts for a REGULAR user are project-scoped server-side, so a summary
   * requested without a project counts nothing and the bell reads 0 even with an
   * open overheat. Passing the loaded project is what makes the badge mean
   * "alerts on what you are looking at".
   *
   * Sent unconditionally rather than only for regular users: the client does not
   * decide scope, the server does. DataServices returns customer-wide results on
   * the ADMIN path regardless of this parameter, so an admin keeps seeing
   * everything — and the client never has to know which role it is serving.
   */
  private currentProject(): string {
    return this.projectService.selectedProjectName ?? '';
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // ── Polling lifecycle ──────────────────────────────────────────────────────

  startPolling(): void {
    if (this.polling) {
      return;
    }
    this.polling = true;

    timer(0, AlertsService.POLL_INTERVAL_MS)
      .pipe(
        takeUntil(this.stopPolling$),
        // Pause while realtime alerts are disabled in effective preferences.
        filter(() => this.preferencesService.effective$.value.notificationsRealtimeAlerts !== false),
        switchMap(() => this.fetchSummary()),
      )
      .subscribe((summary) => {
        if (summary) {
          this.summary$.next(summary);
        }
      });

    // Re-poll immediately when the PROJECT CHANGES. Without this the badge keeps
    // the previous project's count for up to a full poll interval, which reads as
    // the alert following you between projects.
    //
    // THREE FETCHES PER PROJECT-OPEN, measured live, and this subscription was two
    // of them. selectedProject is a BehaviorSubject, so subscribing REPLAYS the
    // current value — a fetch the timer(0) above has already just made — and then
    // the open itself emits again. skip(1) drops the replay; distinctUntilChanged
    // drops a re-emission of the same project, which happens on any path that
    // re-announces the current selection without changing it.
    //
    // The customer subscription below already avoided the replay, with a
    // lastCustomerId sentinel. Same problem, and it was solved there and not here.
    this.projectService.selectedProject
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.stopPolling$))
      .subscribe(() => this.refreshSummary());

    // Re-poll immediately when the active customer changes.
    this.userAuth.activeCustomer$
      .pipe(takeUntil(this.stopPolling$))
      .subscribe((customer) => {
        const id = customer?.CustomerId ?? '';
        if (this.lastCustomerId !== null && id !== this.lastCustomerId) {
          this.refreshSummary();
        }
        this.lastCustomerId = id;
      });
  }

  stopPolling(): void {
    if (!this.polling) {
      return;
    }
    this.polling = false;
    this.lastCustomerId = null;
    this.stopPolling$.next();
  }

  /** One-shot summary refresh (used after acknowledge and on customer switch). */
  refreshSummary(): void {
    this.fetchSummary()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((summary) => {
        if (summary) {
          this.summary$.next(summary);
        }
      });
  }

  // ── API calls ──────────────────────────────────────────────────────────────

  getAlerts(state: AlertStateFilter = 'open', since?: number, limit?: number): Observable<Alert[]> {
    let params = this.identityParams();
    if (!params) {
      return of([]);
    }
    params = this.withProjectScope(params);
    params = params.set('state', state);
    if (since != null) {
      params = params.set('since', String(since));
    }
    if (limit != null) {
      params = params.set('limit', String(limit));
    }

    return this.http
      .get<Alert[]>(`${environment.apiUrl}v1/alerts`, { params })
      .pipe(
        catchError((err) => {
          console.warn('AlertsService > getAlerts failed', err);
          return of([]);
        }),
      );
  }

  acknowledge(alertId: string): Observable<Alert | null> {
    const body = this.identityBody();
    if (!body) {
      return of(null);
    }
    const params = new HttpParams();

    return this.http
      .post<Alert>(`${environment.apiUrl}v1/alerts/${alertId}/acknowledge`, body, { params })
      .pipe(
        tap(() => this.refreshSummary()),
        catchError((err) => {
          console.error('AlertsService > acknowledge failed', err);
          return of(null);
        }),
      );
  }

  /**
   * Acknowledge every open alert in the caller's scope.
   *
   * `project` narrows it to one project. A REGULAR user must pass the project they have open (Joel,
   * 2026-10-08: "Acknowledge all for <project>"; DataServices answers 400 project-required to a
   * regular user without one). An ADMIN's "Acknowledge all" is customer-wide, so the caller omits
   * it: DataServices honours a project from an admin too (f858d41), so sending the open project
   * would silently narrow an admin's customer-wide action. Unlike the reads, which carry the open
   * project unconditionally, this one is chosen by the caller.
   */
  acknowledgeAll(project?: string): Observable<{ acknowledged: number } | null> {
    const identity = this.identityBody();
    if (!identity) {
      return of(null);
    }
    const body = project ? { ...identity, project } : identity;
    const params = new HttpParams();

    return this.http
      .post<{ acknowledged: number }>(`${environment.apiUrl}v1/alerts/acknowledge-all`, body, { params })
      .pipe(
        tap(() => this.refreshSummary()),
        catchError((err) => {
          console.error('AlertsService > acknowledgeAll failed', err);
          return of(null);
        }),
      );
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  /**
   * Scope a request to the loaded project when there is one.
   *
   * Omitted entirely when nothing is loaded, rather than sent empty: an empty
   * project is not a filter, and the server should see the same request it saw
   * before this change.
   */
  private withProjectScope(params: HttpParams): HttpParams {
    const project = this.currentProject();
    return project ? params.set('project', project) : params;
  }

  private fetchSummary(): Observable<AlertSummary | null> {
    let params = this.identityParams();
    if (!params) {
      return of(null);
    }
    params = this.withProjectScope(params);
    return this.http
      .get<AlertSummary>(`${environment.apiUrl}v1/alerts/summary`, { params })
      .pipe(
        catchError((err) => {
          console.warn('AlertsService > summary fetch failed', err);
          return of(null);
        }),
      );
  }

  private identityParams(): HttpParams | null {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.userAuth.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();

    if (!tenantId || !objectId) {
      return null;
    }

    let params = new HttpParams()
      .set('tenantId', tenantId)
      .set('objectId', objectId);
    if (customerId) {
      params = params.set('customerId', customerId);
    }
    return params;
  }

  private identityBody(): { tenantId: string; objectId: string; customerId: string | null } | null {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.userAuth.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();

    if (!tenantId || !objectId) {
      return null;
    }
    return { tenantId, objectId, customerId: customerId || null };
  }
}
